import { Button } from '@mui/material'
import moment from 'moment-timezone'
import { Dispatch, FC, SetStateAction, useContext, useMemo } from 'react'

import { AppContext } from 'providers/context/App'
import { triggerHealthCheckYesOrNoEvent } from 'services/Analytics'
import { CUSTOMER_SESSION_TOKEN } from 'utils/constants/Helpers'
import { serviceIDs } from 'utils/constants/services'
import { triggerKlaviyoEvent } from 'utils/helper-functions/klaviyoEvents'
import { KlaviyoDataPayload } from 'utils/types/klaviyoPayloadTypes'
import {
  GA4_EVENTS,
  HEALTH_CHECK,
  KLAVIYO_EVENTS,
} from '../../../utils/constants/Enums'
import {
  conditionalClasses,
  generateAbandonUrl,
  getLocalstorageItem,
} from '../../../utils/helper-functions'

import './health-check.scss'

interface Props {
  healthCheck: HEALTH_CHECK
  setHealthCheck: Dispatch<SetStateAction<HEALTH_CHECK>>
  navigateToAppointment: () => void
  navigateToDateSelection: () => void
  isMicroNeedling: boolean
}

const HealthCheckFooter: FC<Props> = ({
  healthCheck,
  setHealthCheck,
  navigateToAppointment,
  navigateToDateSelection,
  isMicroNeedling,
}) => {
  const { appMasterData } = useContext(AppContext)
  const locationName = appMasterData.location.name
  const userId = appMasterData.userInfo.boulevard_id ?? ''
  const isFullEditFacial = useMemo(
    () => serviceIDs?.[6] === appMasterData?.category?.categoryId,
    [appMasterData?.category?.categoryId]
  )

  const handleKlaviyoEvent = async () => {
    const isUserLogin = Boolean(getLocalstorageItem(CUSTOMER_SESSION_TOKEN))
    if (isUserLogin) {
      const payloadData: KlaviyoDataPayload = {
        clientId: appMasterData.userInfo.boulevard_id,
        email: appMasterData.userInfo.email,
        location: appMasterData.location.name,
        serviceId: appMasterData.category.categoryId,
        serviceName: appMasterData.category.categoryObj?.name,
        selectedDateTime: appMasterData.selectedTime.startTime,
        abandonLink: generateAbandonUrl(
          KLAVIYO_EVENTS.CONTRAINDICATIONS_SCREEN_SELECTED,
          appMasterData,
          moment
            .parseZone(appMasterData.selectedTime.startTime)
            .utc(true)
            .format()
        ),
      }
      await triggerKlaviyoEvent(
        KLAVIYO_EVENTS.CONTRAINDICATIONS_SCREEN_SELECTED,
        payloadData
      )
    }
  }

  const onChoose = () => {
    handleKlaviyoEvent()
    navigateToDateSelection()
  }

  const onYes = () => {
    window.scrollTo(0, 0)
    triggerHealthCheckYesOrNoEvent(
      locationName,
      GA4_EVENTS.HEALTH_CHECK_YES_GA4,
      userId,
      true
    )
    setHealthCheck(HEALTH_CHECK.YES)
  }

  const onNo = () => {
    triggerHealthCheckYesOrNoEvent(
      locationName,
      GA4_EVENTS.HEALTH_CHECK_NO_GA4,
      userId,
      false
    )
    handleKlaviyoEvent()
    navigateToAppointment()
  }

  return (
    <div
      className={conditionalClasses(
        healthCheck !== HEALTH_CHECK.YES,
        'dual-buttons',
        'single-button',
        'footer-continue health-check-footer'
      )}
    >
      {healthCheck === HEALTH_CHECK.YES ? (
        <Button
          fullWidth
          variant="contained"
          disableElevation
          className="app-btn choose-btn"
          onClick={onChoose}
        >
          Choose Another{' '}
          {isMicroNeedling || isFullEditFacial ? 'Service' : 'Date'}
        </Button>
      ) : (
        <>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            className="app-btn app-btn-outlined yes-btn"
            onClick={onYes}
          >
            Yes
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            className="app-btn no-btn"
            onClick={onNo}
          >
            No
          </Button>
        </>
      )}
    </div>
  )
}

export default HealthCheckFooter
